import type { inferRouterInputs } from "@trpc/server";
import { useCallback, useRef } from "react";
import type { TrpcRouter } from "server/routers";
import { useSession } from "@/app/api/authClient";
import { trpc } from "@/app/api/trpcClient";

type LogEventInput = inferRouterInputs<TrpcRouter>["accessMetrics"]["logEvent"];

interface LogOptions {
  dedupe?: boolean;
}

export function useAccessLogger() {
  const { data: session } = useSession();
  const { mutate: logEventMutate, isPending } = trpc.accessMetrics.logEvent.useMutation();
  const lastEventKeyRef = useRef<string | null>(null);

  const logEvent = useCallback(
    (event: LogEventInput, options: LogOptions = {}) => {
      if (!session?.user?.id) return;

      const payload = {
        scope: event.scope,
        target: event.target,
        action: event.action,
        metadata: event.metadata,
      } satisfies LogEventInput;

      if (options.dedupe) {
        const key = JSON.stringify(payload);
        if (lastEventKeyRef.current === key) {
          return;
        }
        lastEventKeyRef.current = key;
      }

      logEventMutate(payload);
    },
    [logEventMutate, session?.user?.id],
  );

  const trackPageView = useCallback(
    (target: string, metadata?: Record<string, unknown>) => {
      logEvent({ scope: "page", target, action: "view", metadata }, { dedupe: true });
    },
    [logEvent],
  );

  const trackComponentOpen = useCallback(
    (target: string, metadata?: Record<string, unknown>) => {
      logEvent({ scope: "component", target, action: "open", metadata });
    },
    [logEvent],
  );

  const trackButtonClick = useCallback(
    (target: string, metadata?: Record<string, unknown>) => {
      logEvent({ scope: "button", target, action: "click", metadata });
    },
    [logEvent],
  );

  return {
    logEvent,
    trackPageView,
    trackComponentOpen,
    trackButtonClick,
    isLogging: isPending,
  };
}
